import type { ISiteSettings } from "@contentful-api/types/contentful";
import { ModalTypes } from "@lib/enums/ModalTypes";
import dynamic from "next/dynamic";

export interface IAuthorizationModules {
    offerToLogIn: ISiteSettings["fields"]["offerToLogIn"];
    offerToCreateAnAccount: ISiteSettings["fields"]["offerToCreateAnAccount"];
    policiesAgreementTerms: ISiteSettings["fields"]["policiesAgreementTerms"];
    authorizationInvitation: ISiteSettings["fields"]["authorizationInvitation"];
    forgotYourPassword: ISiteSettings["fields"]["forgotYourPassword"];
    eccoNewsletter: ISiteSettings["fields"]["eccoNewsletter"];
    eccoMembership: ISiteSettings["fields"]["eccoMembership"];
}

export interface IContactFormModules {
    contactFormAgreement: ISiteSettings["fields"]["contactFormAgreement"];
    reCaptchaAgreement: ISiteSettings["fields"]["reCaptchaAgreement"];
}

export interface ILiveChatModal {
    liveChatAgreement: ISiteSettings["fields"]["liveChatAgreement"];
    liveChatOfflineDescription: ISiteSettings["fields"]["liveChatOfflineDescription"];
}

const LogInModal = dynamic(() => import("@components/auth/login/LogInModal"), { ssr: false });
const SignUpModal = dynamic(() => import("@components/auth/sign-up/SignUpModal"), { ssr: false });
const LoggedOutModal = dynamic(
    () => import("@components/ui/modals/logged-out-modal/LoggedOutModal"),
    { ssr: false }
);
const ThankYouForSignUpModal = dynamic(
    () => import("@components/ui/modals/thank-you-modal/ThankYouForSignUpModal"),
    { ssr: false }
);
const SizeGuideModal = dynamic(() => import("@components/ui/size-guide-modal/SizeGuideModal"), {
    ssr: false,
});
const GlobalSizeGuideModal = dynamic(
    () => import("@components/ui/size-guide-modal/GlobalSizeGuideModal"),
    { ssr: false }
);
const ChatIsNotAvailable = dynamic(() => import("@components/ui/live-chat/ChatIsNotAvailable"), {
    ssr: false,
});
const LiveChatForm = dynamic(() => import("../ui/live-chat/LiveChatForm"), { ssr: false });
const Chat = dynamic(() => import("../ui/live-chat/Chat"), { ssr: false });
const ShippingDetails = dynamic(
    () => import("../commerce/checkout/delivery-step/ShippingDetailsModal"),
    { ssr: false }
);
const BaseModal = dynamic(() => import("@components/ui/modals/base-modal/BaseModal"), {
    ssr: false,
});
const GiftWrappingModal = dynamic(
    () => import("@components/ui/modals/gift-wrapping/GiftWrappingModal"),
    { ssr: false }
);
const AddedToWishlistModal = dynamic(
    () => import("@components/ui/modals/add-to-wishlist-modal/AddedToWishlistModal"),
    { ssr: false }
);

const ContactForm = dynamic(
    () => import("@components/ui/modals/contact-form/ContactForm").then((mod) => mod.ContactForm),
    { ssr: false }
);
const ContactFormSubmitted = dynamic(
    () =>
        import("@components/ui/modals/contact-form/ContactFormSubmitted").then(
            (mod) => mod.ContactFormSubmitted
        ),
    { ssr: false }
);
const ForgotPassword = dynamic(() => import("../auth/login/ForgotPassword"));
const upgradeAccountModal = dynamic(() => import("@components/auth/loyalty/UpgradeAccountModal"));
const EccoPassModal = dynamic(() => import("@components/ui/modals/ecco-club/EccoPassModal"));
const EccoClubBalanceHistoryModal = dynamic(
    () => import("@components/ui/modals/ecco-club/BalanceHistoryModal")
);

type ModalMap = {
    [key in ModalTypes]?: React.ComponentType<any>;
};

export const modalMap: ModalMap = {
    [ModalTypes.login]: LogInModal,
    [ModalTypes.signOut]: LoggedOutModal,
    [ModalTypes.sizeGuide]: SizeGuideModal,
    [ModalTypes.globalSizeGuide]: GlobalSizeGuideModal,
    [ModalTypes.createAccount]: SignUpModal,
    [ModalTypes.thankYouForSignUp]: ThankYouForSignUpModal,
    [ModalTypes.forgotPassword]: ForgotPassword,
    [ModalTypes.contactForm]: ContactForm,
    [ModalTypes.contactFormSubmitted]: ContactFormSubmitted,
    [ModalTypes.shippingDetails]: ShippingDetails,
    [ModalTypes.liveChat]: LiveChatForm,
    [ModalTypes.chatIsNotAvailable]: ChatIsNotAvailable,
    [ModalTypes.chat]: Chat,
    [ModalTypes.giftWrapping]: GiftWrappingModal,
    [ModalTypes.addToWishList]: AddedToWishlistModal,
    [ModalTypes.base]: BaseModal,
    [ModalTypes.upgradeAccount]: upgradeAccountModal,
    [ModalTypes.eccoPass]: EccoPassModal,
    [ModalTypes.eccoClubBalanceHistory]: EccoClubBalanceHistoryModal,
};
